import React, { useState, useMemo, useEffect } from 'react';
import { Delegation } from '../types/settings';

interface GovernoratOption {
  id: number;
  fr: string;
  ar: string;
  delegations: Delegation[];
}

interface SettingsProps {
  currentDelegationId: number;
  onDelegationChange: (delegationId: number, governoratId: number) => void;
  renderTrigger: (openModal: () => void) => React.ReactNode;
}

// Embedded Tunisia regions data
const ALL_REGIONS: GovernoratOption[] = [
  {
    id: 358, fr: "Ariana", ar: "اريانة",
    delegations: [
      { id: 631, name: "Ariana", arabicName: "اريانة" },
      { id: 534, name: "Attadhamon", arabicName: "التظامن" },
      { id: 530, name: "Kalaat El Andalous", arabicName: "قلعة الأندلس" },
      { id: 533, name: "Mnihla", arabicName: "المنيهلة" },
      { id: 531, name: "Raoued", arabicName: "رواد" },
      { id: 535, name: "Sidi Thabet", arabicName: "سيدي ثابت" },
      { id: 532, name: "Soukra", arabicName: "سكرة" }
    ]
  },
  {
    id: 356, fr: "Béja", ar: "باجة",
    delegations: [
      { id: 515, name: "Amdoun", arabicName: "عمدون" },
      { id: 629, name: "Beja", arabicName: "باجة" },
      { id: 519, name: "Goubellat", arabicName: "قبلاط" },
      { id: 520, name: "Mejez El Bab", arabicName: "مجاز الباب" },
      { id: 514, name: "Nefza", arabicName: "نفزة" },
      { id: 518, name: "Teboursouk", arabicName: "تبرسق" },
      { id: 517, name: "Testour", arabicName: "تستور" },
      { id: 516, name: "Tibar", arabicName: "تيبار" }
    ]
  },
  {
    id: 349, fr: "Ben Arous", ar: "بن عروس",
    delegations: [
      { id: 622, name: "Ben Arous", arabicName: "بن عروس" },
      { id: 453, name: "Boumhel Bassatine", arabicName: "بومهل البساتين" },
      { id: 447, name: "El Mourouj", arabicName: "المروج" },
      { id: 450, name: "Ezzahra", arabicName: "الزهرة" },
      { id: 446, name: "Fouchana", arabicName: "فوشانة" },
      { id: 452, name: "Hammam Chatt", arabicName: "حمام الشط" },
      { id: 451, name: "Hammam Lif", arabicName: "حمام الأنف" },
      { id: 454, name: "Medina Jedida", arabicName: "المدينة الجديدة" },
      { id: 448, name: "Megrine", arabicName: "مقرين" },
      { id: 445, name: "Mohamedia", arabicName: "المحمدية" },
      { id: 444, name: "Mornag", arabicName: "مرناق" },
      { id: 449, name: "Rades", arabicName: "رادس" }
    ]
  },
  {
    id: 361, fr: "Bizerte", ar: "بنزرت",
    delegations: [
      { id: 572, name: "Al Khitmin", arabicName: "الختمين" },
      { id: 569, name: "Al Matlin", arabicName: "المتلين" },
      { id: 571, name: "Aousja", arabicName: "عوسجة" },
      { id: 634, name: "Bizerte", arabicName: "بنزرت" },
      { id: 566, name: "El Alia", arabicName: "العالية" },
      { id: 563, name: "Ghar El Melh", arabicName: "غار الملح" },
      { id: 560, name: "Ghezala", arabicName: "غزالة" },
      { id: 558, name: "Joumine", arabicName: "جومين" },
      { id: 559, name: "Mateur", arabicName: "ماطر" },
      { id: 562, name: "Menzel Bourguiba", arabicName: "منزل برقيبة" },
      { id: 561, name: "Menzel Jemil", arabicName: "منزل جميل" },
      { id: 564, name: "Ras Jebel", arabicName: "رأس الجبل" },
      { id: 557, name: "Sejnane", arabicName: "سجنان" },
      { id: 565, name: "Tinja", arabicName: "تينجة" },
      { id: 567, name: "Utique", arabicName: "أوتيك" },
      { id: 568, name: "Zarzouna", arabicName: "جرزونة" },
      { id: 570, name: "Zouaouine", arabicName: "الزواوين" }
    ]
  },
  {
    id: 347, fr: "Gabes", ar: "قابس",
    delegations: [
      { id: 1024, name: "El Hamma", arabicName: "الحامة" },
      { id: 620, name: "Gabes", arabicName: "قابس" },
      { id: 1019, name: "Ghannouch", arabicName: "غنوش" },
      { id: 1020, name: "Mareth", arabicName: "مارث" },
      { id: 1022, name: "Matmata", arabicName: "مطماطا" },
      { id: 1023, name: "Matmata Nouvelle", arabicName: "مطماطا الجديدة" },
      { id: 1021, name: "Menzel Habib", arabicName: "منزل حبيب" },
      { id: 1025, name: "Metouia", arabicName: "المطوية" }
    ]
  },
  {
    id: 365, fr: "Gafsa", ar: "قفصة",
    delegations: [
      { id: 612, name: "Belkhir", arabicName: "بلخير" },
      { id: 611, name: "El Guettar", arabicName: "القطار" },
      { id: 608, name: "El Ksar", arabicName: "القصر" },
      { id: 638, name: "Gafsa", arabicName: "قفصة" },
      { id: 607, name: "Mdhilla", arabicName: "المظيلة" },
      { id: 606, name: "Metlaoui", arabicName: "المتلوي" },
      { id: 604, name: "Moulares", arabicName: "أم العرايس" },
      { id: 605, name: "Redyef", arabicName: "الرديف" },
      { id: 609, name: "Sened", arabicName: "السند" },
      { id: 610, name: "Sidi Aich", arabicName: "سيدي عيش" }
    ]
  },
  {
    id: 354, fr: "Jendouba", ar: "جندوبة",
    delegations: [
      { id: 498, name: "Ain Drahem", arabicName: "عين دراهم" },
      { id: 503, name: "Balta Bou Aouane", arabicName: "بلطة بوعوان" },
      { id: 499, name: "Bou Salem", arabicName: "بوسالم" },
      { id: 502, name: "Fernana", arabicName: "فرنانة" },
      { id: 501, name: "Ghardimaou", arabicName: "غار الدماء" },
      { id: 627, name: "Jendouba", arabicName: "جندوبة" },
      { id: 500, name: "Oued Mliz", arabicName: "واد مليز" },
      { id: 497, name: "Tabarka", arabicName: "طبرقة" }
    ]
  },
  {
    id: 357, fr: "Kairouan", ar: "القيروان",
    delegations: [
      { id: 528, name: "Bou Hajla", arabicName: "بوحجلة" },
      { id: 522, name: "Chebika", arabicName: "الشبيكة" },
      { id: 529, name: "Chrarda", arabicName: "الشراردة" },
      { id: 524, name: "El Alaa", arabicName: "العلاء" },
      { id: 523, name: "Haffouz", arabicName: "حفوز" },
      { id: 526, name: "Hajeb El Ayoun", arabicName: "حاجب العيون" },
      { id: 630, name: "Kairouan", arabicName: "القيروان" },
      { id: 527, name: "Nasrallah", arabicName: "نصرالله" },
      { id: 525, name: "Oueslatia", arabicName: "الوسلاتية" },
      { id: 521, name: "Sebikha", arabicName: "السبيخة" }
    ]
  },
  {
    id: 362, fr: "Kasserine", ar: "القصرين",
    delegations: [
      { id: 579, name: "El Ayoun", arabicName: "العيون" },
      { id: 577, name: "Feriana", arabicName: "فريانة" },
      { id: 578, name: "Foussana", arabicName: "فوسانة" },
      { id: 582, name: "Hassi Ferid", arabicName: "حاسي الفريد" },
      { id: 574, name: "Hidra", arabicName: "حيدرة" },
      { id: 581, name: "Jedliane", arabicName: "جدليان" },
      { id: 635, name: "Kasserine", arabicName: "القصرين" },
      { id: 580, name: "Majel Bel Abbes", arabicName: "ماجل بلعباس" },
      { id: 576, name: "Sbiba", arabicName: "سبيبة" },
      { id: 575, name: "Sbitla", arabicName: "سبيطلة" },
      { id: 573, name: "Tala", arabicName: "تالة" }
    ]
  },
  {
    id: 1012, fr: "Kébili", ar: "قبلي",
    delegations: [
      { id: 484, name: "Douz", arabicName: "دوز" },
      { id: 485, name: "El Faouar", arabicName: "الفوار" },
      { id: 482, name: "Kebili", arabicName: "قبلي" },
      { id: 486, name: "Matrouha", arabicName: "المطروحة" },
      { id: 487, name: "Rjim Maatoug", arabicName: "رجيم معتوق" },
      { id: 483, name: "Souk El Ahad", arabicName: "سوق الأحد" }
    ]
  },
  {
    id: 363, fr: "Le kef", ar: "الكاف",
    delegations: [
      { id: 590, name: "Dahmani", arabicName: "الدهماني" },
      { id: 583, name: "El Ksour", arabicName: "القصور" },
      { id: 585, name: "Jerissa", arabicName: "الجريسة" },
      { id: 587, name: "Kalâa El Khasba", arabicName: "القلعة الخصبة" },
      { id: 588, name: "Kalaat Sinane", arabicName: "قلعة السنان" },
      { id: 636, name: "Le Kef", arabicName: "الكاف" },
      { id: 584, name: "Le Sers", arabicName: "السرس" },
      { id: 589, name: "Nebeur", arabicName: "نبر" },
      { id: 591, name: "Sakiet Sidi Youssef", arabicName: "ساقية سيدي يوسف" },
      { id: 586, name: "Tejerouine", arabicName: "تاجروين" },
      { id: 1523, name: "Tourief", arabicName: "الطويرف" }
    ]
  },
  {
    id: 345, fr: "Mahdia", ar: "المهدية",
    delegations: [
      { id: 427, name: "Boumerdes", arabicName: "بومرداس" },
      { id: 424, name: "Chebba", arabicName: "الشابة" },
      { id: 429, name: "Chorbane", arabicName: "شربان" },
      { id: 619, name: "El Jem", arabicName: "الجم" },
      { id: 430, name: "Hbira", arabicName: "هبيرة" },
      { id: 423, name: "Ksour Essef", arabicName: "قصور الساف" },
      { id: 618, name: "Mahdia", arabicName: "المهدية" },
      { id: 425, name: "Malloulech", arabicName: "مللولش" },
      { id: 431, name: "Ouled Chamekh", arabicName: "أولاد شامخ" },
      { id: 1084, name: "Rejiche", arabicName: "الرجيش" },
      { id: 426, name: "Sidi Alouane", arabicName: "سيدي علوان" },
      { id: 428, name: "Souassi", arabicName: "السواسي" }
    ]
  },
  {
    id: 360, fr: "Manouba", ar: "منوبة",
    delegations: [
      { id: 552, name: "Borj El Amri", arabicName: "برج العامري" },
      { id: 554, name: "Douar Hicher", arabicName: "دوار هيشر" },
      { id: 551, name: "El Battan", arabicName: "البطان" },
      { id: 550, name: "Jedaida", arabicName: "الجديدة" },
      { id: 633, name: "Manouba", arabicName: "منوبة" },
      { id: 555, name: "Mornaguia", arabicName: "المرناقية" },
      { id: 553, name: "Oued Ellil", arabicName: "واد الليل" },
      { id: 556, name: "Tebourba", arabicName: "طبربة" }
    ]
  },
  {
    id: 353, fr: "Medenine", ar: "مدنين",
    delegations: [
      { id: 495, name: "Arram", arabicName: "العرام" },
      { id: 488, name: "Ben Guerdene", arabicName: "بن قردان" },
      { id: 489, name: "Beni Khedach", arabicName: "بني خداش" },
      { id: 493, name: "Djerba Ajim", arabicName: "جربة آجيم" },
      { id: 491, name: "Djerba El May", arabicName: "جربة الماي" },
      { id: 494, name: "Djerba Houmt Souk", arabicName: "جربة حومة السوق" },
      { id: 492, name: "Djerba Midoun", arabicName: "جربة ميدون" },
      { id: 626, name: "Medenine", arabicName: "مدنين" },
      { id: 496, name: "Sidi Makhlouf", arabicName: "سيدي مخلوف" },
      { id: 490, name: "Zarzis", arabicName: "جرجيس" }
    ]
  },
  {
    id: 364, fr: "Monastir", ar: "المنستير",
    delegations: [
      { id: 601, name: "Bekalta", arabicName: "البقالطة" },
      { id: 603, name: "Benbla", arabicName: "بنبلة" },
      { id: 597, name: "Beni Hassan", arabicName: "بني حسان" },
      { id: 595, name: "Jammel", arabicName: "جمال" },
      { id: 602, name: "Ksar Helal", arabicName: "قصر هلال" },
      { id: 599, name: "Ksibet Medyouni", arabicName: "قصيبة المديوني" },
      { id: 598, name: "Moknine", arabicName: "المكنين" },
      { id: 637, name: "Monastir", arabicName: "المنستير" },
      { id: 593, name: "Ouerdanine", arabicName: "الوردانين" },
      { id: 592, name: "Sahline", arabicName: "الساحلين" },
      { id: 600, name: "Sayada Lamta Bouhjar", arabicName: "صيادة" },
      { id: 594, name: "Teboulba", arabicName: "طبلبة" },
      { id: 596, name: "Zeramdine", arabicName: "زرامدين" }
    ]
  },
  {
    id: 350, fr: "Nabeul", ar: "نابل",
    delegations: [
      { id: 467, name: "Beni Khalled", arabicName: "بني خلاد" },
      { id: 460, name: "Beni Khiar", arabicName: "بني خيار" },
      { id: 463, name: "Bou Argoub", arabicName: "بوعرقوب" },
      { id: 468, name: "Dar Chaabane Elfehri", arabicName: "دار شعبان الفهري" },
      { id: 466, name: "El Haouaria", arabicName: "الهوارية" },
      { id: 457, name: "El Mida", arabicName: "الميدة" },
      { id: 464, name: "Grombalia", arabicName: "قرمبالية" },
      { id: 469, name: "Hammam Ghezaz", arabicName: "حمام الغزاز" },
      { id: 461, name: "Hammamet", arabicName: "الحمامات" },
      { id: 455, name: "Kelibia", arabicName: "قليبية" },
      { id: 459, name: "Korba", arabicName: "قربة" },
      { id: 458, name: "Menzel Bouzelfa", arabicName: "منزل بوزلفة" },
      { id: 456, name: "Menzel Temime", arabicName: "منزل تميم" },
      { id: 623, name: "Nabeul", arabicName: "نابل" },
      { id: 465, name: "Slimane", arabicName: "سليمان" },
      { id: 462, name: "Takelsa", arabicName: "تاكلسة" }
    ]
  },
  {
    id: 359, fr: "Sfax", ar: "صفاقس",
    delegations: [
      { id: 540, name: "Agareb", arabicName: "عقارب" },
      { id: 538, name: "Bir Ali Ben Khelifa", arabicName: "بئر علي بن خليفة" },
      { id: 545, name: "El Amra", arabicName: "العامرة" },
      { id: 541, name: "El Hencha", arabicName: "الحنشة" },
      { id: 539, name: "Ghraiba", arabicName: "الغريبة" },
      { id: 536, name: "Jebiniana", arabicName: "جبنيانة" },
      { id: 543, name: "Kerkennah El Attaya", arabicName: "قرقنة العطايا" },
      { id: 544, name: "Kerkennah Mellita", arabicName: "قرقنة مليتة" },
      { id: 537, name: "Mahres", arabicName: "المحرس" },
      { id: 542, name: "Menzel Chaker", arabicName: "منزل شاكر" },
      { id: 548, name: "Sakiet Eddaier", arabicName: "ساقية الداير" },
      { id: 547, name: "Sakiet Ezzit", arabicName: "ساقية الزيت" },
      { id: 632, name: "Sfax", arabicName: "صفاقس" },
      { id: 546, name: "Skhira", arabicName: "الصخيرة" },
      { id: 549, name: "Tina", arabicName: "تينة" }
    ]
  },
  {
    id: 355, fr: "Sidi Bouzid", ar: "سيدي بوزيد",
    delegations: [
      { id: 508, name: "Bir El Hafey", arabicName: "بئر الحفي" },
      { id: 1522, name: "Hichria", arabicName: "الهيشرية" },
      { id: 506, name: "Jelma", arabicName: "جلمة" },
      { id: 505, name: "Meknassi", arabicName: "المكناسي" },
      { id: 510, name: "Menzel Bouzayane", arabicName: "منزل بو زيان" },
      { id: 511, name: "Mezouna", arabicName: "المزونة" },
      { id: 512, name: "Ouled Haffouz", arabicName: "أولاد حفوز" },
      { id: 504, name: "Regueb", arabicName: "الرقاب" },
      { id: 507, name: "Sabbalet Ouled Asker", arabicName: "سبالة أولاد عسكر" },
      { id: 509, name: "Sidi Ali Ben Aoun", arabicName: "سيدي علي بن عون" },
      { id: 628, name: "Sidi Bouzid", arabicName: "سيدي بوزيد" },
      { id: 513, name: "Souk Jedid", arabicName: "سوق الجديد" }
    ]
  },
  {
    id: 344, fr: "Siliana", ar: "سليانة",
    delegations: [
      { id: 417, name: "Bargou", arabicName: "برقو" },
      { id: 420, name: "Bouarada", arabicName: "بوعرادة" },
      { id: 419, name: "El Aroussa", arabicName: "العروسة" },
      { id: 418, name: "Gaafour", arabicName: "قعفور" },
      { id: 414, name: "Kesra", arabicName: "كسرى" },
      { id: 421, name: "Le Krib", arabicName: "الكريب" },
      { id: 415, name: "Makther", arabicName: "مكثر" },
      { id: 416, name: "Rouhia", arabicName: "الروحية" },
      { id: 422, name: "Sidi Bou Rouis", arabicName: "بورويس" },
      { id: 617, name: "Siliana", arabicName: "سليانة" }
    ]
  },
  {
    id: 348, fr: "Sousse", ar: "سوسة",
    delegations: [
      { id: 437, name: "Akouda", arabicName: "أكودة" },
      { id: 433, name: "Bouficha", arabicName: "بوفيشة" },
      { id: 434, name: "Enfida", arabicName: "النفيضة" },
      { id: 438, name: "Hammam Sousse", arabicName: "حمام سوسة" },
      { id: 435, name: "Hergla", arabicName: "هرقلة" },
      { id: 439, name: "Kalaa Kebira", arabicName: "القلعة الكبيرة" },
      { id: 443, name: "Kalaa Seghira", arabicName: "القلعة الصغيرة" },
      { id: 442, name: "Kondar", arabicName: "كندار" },
      { id: 440, name: "Msaken", arabicName: "مساكن" },
      { id: 436, name: "Sidi Bouali", arabicName: "سيدي بوعلي" },
      { id: 441, name: "Sidi Elheni", arabicName: "سيدي الهاني" },
      { id: 621, name: "Sousse", arabicName: "سوسة" }
    ]
  },
  {
    id: 351, fr: "Tataouine", ar: "تطاوين",
    delegations: [
      { id: 474, name: "Bir Lahmar", arabicName: "بئر الأحمر" },
      { id: 1524, name: "Bni Mhira", arabicName: "بني مهيرة" },
      { id: 475, name: "Borj El Khadra", arabicName: "برج الخضراء" },
      { id: 470, name: "Dehiba", arabicName: "الذهيبة" },
      { id: 476, name: "El Borma", arabicName: "البرمة" },
      { id: 473, name: "Ghomrassen", arabicName: "غمراسن" },
      { id: 471, name: "Remada", arabicName: "رمادة" },
      { id: 472, name: "Smar", arabicName: "السمار" },
      { id: 624, name: "Tataouine", arabicName: "تطاوين" }
    ]
  },
  {
    id: 352, fr: "Tozeur", ar: "توزر",
    delegations: [
      { id: 478, name: "Degueche", arabicName: "دقاش" },
      { id: 481, name: "Hammat Al Jarid", arabicName: "حامة الجريد" },
      { id: 479, name: "Hazoua", arabicName: "حزوة" },
      { id: 477, name: "Nefta", arabicName: "نفطة" },
      { id: 480, name: "Tameghza", arabicName: "تمغزة" },
      { id: 625, name: "Tozeur", arabicName: "توزر" }
    ]
  },
  {
    id: 342, fr: "Tunis", ar: "تونس",
    delegations: [
      { id: 404, name: "Bab Bhar", arabicName: "باب بحر" },
      { id: 395, name: "Bab Souika", arabicName: "باب سويقة" },
      { id: 394, name: "Bardo", arabicName: "باردو" },
      { id: 387, name: "Carthage", arabicName: "قرطاج" },
      { id: 389, name: "Cite El Khadhra", arabicName: "حي الخضراء" },
      { id: 397, name: "El Hrairia", arabicName: "الحرايرية" },
      { id: 400, name: "El Kabaria", arabicName: "كبارية" },
      { id: 390, name: "El Menzah", arabicName: "المنزه" },
      { id: 399, name: "El Ouardia", arabicName: "الوردية" },
      { id: 393, name: "Ettahrir", arabicName: "التحرير" },
      { id: 396, name: "Ezzouhour", arabicName: "الزهور" },
      { id: 401, name: "Jebel Jelloud", arabicName: "جبل جلود" },
      { id: 388, name: "La Goulette", arabicName: "حلق الواد" },
      { id: 386, name: "La Marsa", arabicName: "المرسى" },
      { id: 403, name: "La Medina", arabicName: "المدينة" },
      { id: 406, name: "Le Kram", arabicName: "الكرم" },
      { id: 391, name: "Omrane", arabicName: "العمران" },
      { id: 392, name: "Omrane Superieur", arabicName: "العمران الأعلى" },
      { id: 402, name: "Sidi El Bechir", arabicName: "سيدي البشير" },
      { id: 398, name: "Sidi Hassine", arabicName: "سيدي حسين" },
      { id: 405, name: "Sijoumi", arabicName: "السيجومي" },
      { id: 615, name: "Tunis", arabicName: "مدينة تونس" }
    ]
  },
  {
    id: 343, fr: "Zaghouan", ar: "زغوان",
    delegations: [
      { id: 410, name: "Bir Mcherga", arabicName: "بئر مشارقة" },
      { id: 411, name: "El Fahs", arabicName: "الفحص" },
      { id: 412, name: "Nadhour", arabicName: "الناظور" },
      { id: 413, name: "Saouaf", arabicName: "صواف" },
      { id: 616, name: "Zaghouan", arabicName: "زغوان" },
      { id: 409, name: "Zeriba", arabicName: "الزريبة" }
    ]
  }
];

// Helper to find location info from delegation ID
export const getLocationName = (delegationId: number): { delegationAr: string; governoratAr: string } | null => {
  for (const gouv of ALL_REGIONS) {
    const deleg = gouv.delegations.find(d => d.id === delegationId);
    if (deleg) {
      return {
        delegationAr: deleg.arabicName || deleg.name,
        governoratAr: gouv.ar,
      };
    }
  }
  return null;
};

type WizardStep = 'governorat' | 'delegation';

const Settings: React.FC<SettingsProps> = ({
  currentDelegationId,
  onDelegationChange,
  renderTrigger,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState<WizardStep>('governorat');
  const [selectedGouv, setSelectedGouv] = useState<GovernoratOption | null>(null);
  const [search, setSearch] = useState('');

  // Find current selection
  const currentSelection = useMemo(() => {
    for (const gouv of ALL_REGIONS) {
      const deleg = gouv.delegations.find(d => d.id === currentDelegationId);
      if (deleg) {
        return { governorat: gouv, delegation: deleg };
      }
    }
    return null;
  }, [currentDelegationId]);

  // Init selected governorat from current delegation
  useEffect(() => {
    if (currentSelection && !selectedGouv) {
      setSelectedGouv(currentSelection.governorat);
    }
  }, [currentSelection, selectedGouv]);

  // Filter governorats
  const filteredGovernorats = useMemo(() => {
    if (!search.trim()) return ALL_REGIONS;
    const query = search.toLowerCase();
    return ALL_REGIONS.filter(gouv =>
      gouv.fr.toLowerCase().includes(query) || gouv.ar.includes(search)
    );
  }, [search]);

  const openModal = () => {
    setStep('governorat');
    setSearch('');
    setIsModalOpen(true);
  };

  const handleGovernoratSelect = (gouv: GovernoratOption) => {
    setSelectedGouv(gouv);
    setSearch('');
    setStep('delegation');
  };

  const handleDelegationSelect = (deleg: Delegation) => {
    if (selectedGouv) {
      onDelegationChange(deleg.id, selectedGouv.id);
    }
    setIsModalOpen(false);
  };

  const goBackToGovernorat = () => {
    setStep('governorat');
    setSearch('');
  };

  return (
    <>
      {renderTrigger(openModal)}

      {/* Full-screen Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50" dir="rtl">
          {/* Full-screen background */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950" />

          {/* Content */}
          <div className="relative h-full flex flex-col animate-fade-in">
            {/* Header */}
            <div className="flex-shrink-0 px-5 pt-safe-top">
              <div className="flex items-center justify-between pt-5 pb-4">
                {/* Right side: back or close */}
                {step === 'delegation' ? (
                  <button
                    onClick={goBackToGovernorat}
                    className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors py-2"
                  >
                    <svg className="w-5 h-5 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span className="text-sm font-bold">الولايات</span>
                  </button>
                ) : (
                  <div />
                )}

                {/* Left side: close */}
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Title + step indicator */}
              <div className="pb-4">
                {step === 'governorat' ? (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-7 h-7 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">١</span>
                      <span className="text-white/40 text-xs">الخطوة الأولى</span>
                    </div>
                    <h2 className="text-2xl font-bold text-white">اختر الولاية</h2>
                    <p className="text-white/40 text-sm mt-1">اختر الولاية التي تريد معرفة أوقات الصلاة فيها</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-7 h-7 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">٢</span>
                      <span className="text-white/40 text-xs">الخطوة الثانية</span>
                    </div>
                    <h2 className="text-2xl font-bold text-white">اختر المعتمدية</h2>
                    <p className="text-white/40 text-sm mt-1">
                      <span className="text-emerald-400 font-bold">{selectedGouv?.ar}</span>
                      {' ← '}اختر المعتمدية
                    </p>
                  </>
                )}
              </div>

              {/* Search (only on governorat step) */}
              {step === 'governorat' && (
                <div className="relative pb-4">
                  <svg className="absolute right-3 top-3 w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="ابحث عن الولاية..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pr-10 pl-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-white/30 text-sm focus:outline-none focus:border-emerald-400/50 focus:ring-1 focus:ring-emerald-400/30 transition-all"
                  />
                </div>
              )}

              {/* Divider */}
              <div className="h-px bg-white/10" />
            </div>

            {/* Scrollable tag area */}
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {step === 'governorat' ? (
                /* === GOVERNORAT TAGS === */
                <div className="flex flex-wrap gap-2.5">
                  {filteredGovernorats.map((gouv, index) => {
                    const isCurrent = currentSelection?.governorat.id === gouv.id;
                    return (
                      <button
                        key={gouv.id}
                        onClick={() => handleGovernoratSelect(gouv)}
                        className="animate-fade-in-up"
                        style={{ animationDelay: `${index * 20}ms` }}
                      >
                        <div className={`
                          px-5 py-3 rounded-2xl transition-all duration-200 active:scale-95 border
                          ${isCurrent
                            ? 'bg-emerald-500/20 border-emerald-500/40 shadow-lg shadow-emerald-500/10'
                            : 'bg-white/[0.06] border-white/10 hover:bg-white/[0.12] hover:border-white/20'
                          }
                        `}>
                          <span className={`block text-base font-bold leading-tight ${isCurrent ? 'text-emerald-400' : 'text-white/90'}`}>
                            {gouv.ar}
                          </span>
                          <span className={`block text-[11px] mt-1 leading-tight ${isCurrent ? 'text-emerald-400/60' : 'text-white/35'}`}>
                            {gouv.fr}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                  {filteredGovernorats.length === 0 && (
                    <div className="w-full text-center py-12 text-white/30 text-sm">
                      لم يتم العثور على نتائج
                    </div>
                  )}
                </div>
              ) : (
                /* === DELEGATION TAGS === */
                <div className="flex flex-wrap gap-2.5">
                  {selectedGouv?.delegations.map((deleg, index) => {
                    const isSelected = currentDelegationId === deleg.id;
                    return (
                      <button
                        key={deleg.id}
                        onClick={() => handleDelegationSelect(deleg)}
                        className="animate-fade-in-up"
                        style={{ animationDelay: `${index * 25}ms` }}
                      >
                        <div className={`
                          px-5 py-3 rounded-2xl transition-all duration-200 active:scale-95 border relative
                          ${isSelected
                            ? 'bg-gradient-to-br from-emerald-500 to-teal-600 border-emerald-400/50 shadow-lg shadow-emerald-500/20'
                            : 'bg-white/[0.06] border-white/10 hover:bg-white/[0.12] hover:border-white/20'
                          }
                        `}>
                          {isSelected && (
                            <span className="absolute -top-1 -left-1 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-gray-900 flex items-center justify-center">
                              <svg className="w-2 h-2 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                          <span className={`block text-base font-bold leading-tight ${isSelected ? 'text-white' : 'text-white/90'}`}>
                            {deleg.arabicName || deleg.name}
                          </span>
                          <span className={`block text-[11px] mt-1 leading-tight ${isSelected ? 'text-white/70' : 'text-white/35'}`}>
                            {deleg.name}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Step dots at bottom */}
            <div className="flex-shrink-0 flex justify-center gap-2 py-4 pb-safe-bottom">
              <div className={`w-2 h-2 rounded-full transition-all ${step === 'governorat' ? 'bg-emerald-400 w-6' : 'bg-white/20'}`} />
              <div className={`w-2 h-2 rounded-full transition-all ${step === 'delegation' ? 'bg-emerald-400 w-6' : 'bg-white/20'}`} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Settings;
